import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Button, Collapse, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../firebase';
import { Task, FeedItem } from '../Interfaces';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import HtmlContentWithCopyButtons from './HtmlContentWithCopyButtons';
import type { SupportDutyTask } from '../../../features/monthly-support/types';
import { monthlySupportApi } from '../../../features/monthly-support/api';
import SupportDutyDialog from '../../monthly-support/SupportDutyDialog';

/** Detect if content contains HTML tags (LLM sometimes returns HTML instead of markdown) */
const containsHtml = (text: string): boolean => /<[a-z][\s\S]*>/i.test(text);

interface TaskDetailViewProps {
  task: Task;
  onClose: () => void;
  heightPercent: number;
  onTaskUpdated?: (updatedTask: Task) => void;
  onTaskDeleted?: (taskId: string) => void;
}

const TaskDetailView: React.FC<TaskDetailViewProps> = ({ task, onClose, heightPercent, onTaskUpdated, onTaskDeleted }) => {
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [updateContent, setUpdateContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const feedEndRef = useRef<HTMLDivElement>(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [editPriority, setEditPriority] = useState(task.priority || 'medium');
  const [editDueDate, setEditDueDate] = useState(task.dueDate || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const supportTask = (task as SupportDutyTask).support ? task as SupportDutyTask : null;
  const [supportOutcome, setSupportOutcome] = useState<'completed' | 'waived' | null>(null);

  const statusOptions = [
    { value: 'pending', label: 'Pending', bg: 'secondary', icon: 'bi-circle' },
    { value: 'in_progress', label: 'In Progress', bg: 'primary', icon: 'bi-arrow-repeat' },
    { value: 'completed', label: 'Completed', bg: 'success', icon: 'bi-check-circle-fill' },
    { value: 'cancelled', label: supportTask ? 'Waived' : 'Cancelled', bg: 'danger', icon: 'bi-x-circle-fill' },
  ];

  const getCurrentStatus = () => {
    const status = task.status || 'pending';
    if (status === 'done') return statusOptions.find(s => s.value === 'completed')!;
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!task || newStatus === task.status) return;
    if (supportTask) {
      if (newStatus === 'completed' || newStatus === 'cancelled') {
        setSupportOutcome(newStatus === 'completed' ? 'completed' : 'waived');
        return;
      }
      try {
        const result = await monthlySupportApi.updateDuty({ taskId: task.id, status: newStatus === 'in_progress' ? 'in_progress' : 'pending' });
        onTaskUpdated?.(result.task);
        toast.success('Monthly duty updated');
      } catch (error) { toast.error(error instanceof Error ? error.message : 'Monthly duty could not be updated'); }
      return;
    }
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, { 
        status: newStatus, 
        lastEdited: new Date().toISOString() 
      });
      const updatedTask = { ...task, status: newStatus as Task['status'], lastEdited: new Date().toISOString() };
      if (onTaskUpdated) onTaskUpdated(updatedTask);
      toast.success(`Task marked as ${statusOptions.find(s => s.value === newStatus)?.label}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleStartEdit = () => {
    if (supportTask) return;
    setEditTitle(task.title);
    setEditDescription(task.description || '');
    setEditPriority(task.priority || 'medium');
    setEditDueDate(task.dueDate || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTitle(task.title);
    setEditDescription(task.description || '');
    setEditPriority(task.priority || 'medium');
    setEditDueDate(task.dueDate || '');
  };

  const handleSaveEdit = async () => {
    if (supportTask) return;
    if (!editTitle.trim()) {
      toast.error('Task title is required');
      return;
    }
    setIsSaving(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      const updates: any = {
        title: editTitle.trim(),
        task: editTitle.trim(), // Legacy field
        description: editDescription.trim(),
        priority: editPriority,
        lastEdited: new Date().toISOString()
      };
      if (editDueDate) {
        updates.dueDate = editDueDate;
      } else {
        updates.dueDate = null;
      }
      await updateDoc(taskRef, updates);
      const updatedTask = { 
        ...task, 
        title: editTitle.trim(),
        description: editDescription.trim(),
        priority: editPriority as Task['priority'],
        dueDate: editDueDate || undefined,
        lastEdited: new Date().toISOString()
      };
      if (onTaskUpdated) onTaskUpdated(updatedTask);
      setIsEditing(false);
      toast.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTask = async () => {
    if (supportTask) return;
    setIsDeleting(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, { 
        status: 'deleted',
        deletedAt: new Date().toISOString()
      });
      toast.success('Task deleted');
      if (onTaskDeleted) onTaskDeleted(task.id);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getPriorityBadge = (priority?: string) => {
    switch (priority) {
      case 'high': return <Badge bg="danger">High Priority</Badge>;
      case 'medium': return <Badge bg="warning">Medium</Badge>;
      case 'low': return <Badge bg="info">Low</Badge>;
      default: return null;
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      if (!timestamp) return 'No date';
      if (timestamp?.toDate) return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
      if (timestamp instanceof Date) return formatDistanceToNow(timestamp, { addSuffix: true });
      if (typeof timestamp === 'number') return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
      if (typeof timestamp === 'string') return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
      if (timestamp?.seconds) return formatDistanceToNow(new Date(timestamp.seconds * 1000), { addSuffix: true });
      return 'Unknown date';
    } catch {
      return 'Date error';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatFeedContent = (content: string): string => {
    if (!content) return '';
    let formatted = content;
    formatted = formatted.replace(/\\n/g, '<br>');
    formatted = formatted.replace(/\n/g, '<br>');
    formatted = formatted.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
    return formatted;
  };

  const handleAddUpdate = async () => {
    if (!task || (!updateContent.trim() && selectedFiles.length === 0)) return;

    setIsSaving(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      let updatedFeed = [...(task.feed || [])];

      if (updateContent.trim()) {
        const newEntry: FeedItem = {
          id: `${Date.now()}`,
          content: updateContent,
          timestamp: Timestamp.now(),
          type: 'update',
          source: 'human',
          info: 'Task update'
        };
        updatedFeed.push(newEntry);
        await updateDoc(taskRef, {
          feed: arrayUnion(newEntry),
          lastEdited: new Date().toISOString()
        });
      }

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const fileId = `${Date.now()}-${i}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `tasks/${task.id}/${fileId}`);

        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => ({ ...prev, [`file_${i}`]: progress }));
            },
            reject,
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const isImage = file.type.startsWith('image/');
              const content = isImage
                ? `📎 Attached image: <strong>${file.name}</strong>`
                : `📎 Attached file: <strong>${file.name}</strong>`;

              const newEntry: FeedItem = {
                id: fileId,
                content,
                timestamp: Timestamp.now(),
                type: 'file',
                source: 'human',
                info: 'File attachment',
                fileUrl: downloadURL,
                filePath: `tasks/${task.id}/${fileId}`,
                attachment: { name: file.name, size: file.size, type: file.type }
              };

              await updateDoc(taskRef, {
                feed: arrayUnion(newEntry),
                lastEdited: new Date().toISOString()
              });

              updatedFeed.push(newEntry);
              setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[`file_${i}`];
                return newProgress;
              });
              resolve();
            }
          );
        });
      }

      const updatedTask = { ...task, feed: updatedFeed, lastEdited: new Date().toISOString() };
      if (onTaskUpdated) onTaskUpdated(updatedTask);

      setUpdateContent('');
      setSelectedFiles([]);
      setShowAddUpdate(false);
      toast.success('Update added successfully!');
    } catch (error) {
      console.error('Error adding update:', error);
      toast.error('Failed to add update');
    } finally {
      setIsSaving(false);
      setUploadProgress({});
    }
  };

  const handleDeleteFeedEntry = async (entryId: string) => {
    if (!task) return;
    try {
      const updatedFeed = task.feed?.filter(entry => entry.id !== entryId) || [];
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, { feed: updatedFeed, lastEdited: new Date().toISOString() });
      
      const updatedTask = { ...task, feed: updatedFeed, lastEdited: new Date().toISOString() };
      if (onTaskUpdated) onTaskUpdated(updatedTask);
      toast.success('Entry deleted');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleDownloadFile = (entry: FeedItem) => {
    if (!entry.fileUrl || !entry.attachment) return;
    const link = document.createElement('a');
    link.href = entry.fileUrl;
    link.download = entry.attachment.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const sortedFeed = useMemo(() => {
    if (!task.feed) return [];
    return [...task.feed].sort((a, b) => {
      const getTime = (ts: any) => {
        if (!ts) return 0;
        if (ts?.toDate) return ts.toDate().getTime();
        if (ts?.seconds) return ts.seconds * 1000;
        if (typeof ts === 'number') return ts;
        return new Date(ts).getTime();
      };
      return getTime(a.timestamp) - getTime(b.timestamp);
    });
  }, [task.feed]);

  return (
    <>
    <div className="d-flex flex-column" style={{ height: `${heightPercent}%`, minHeight: '150px' }}>
      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="bg-body rounded p-4 shadow-lg" style={{ maxWidth: '300px' }}>
            <h6 className="text-body mb-3">Delete Task?</h6>
            <p className="text-muted small mb-3">This will mark the task as deleted. This action cannot be undone.</p>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="light" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDeleteTask} disabled={isDeleting}>
                {isDeleting ? <Spinner size="sm" animation="border" /> : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Header */}
      <div className="p-4 border-bottom bg-body d-flex justify-content-between align-items-start">
        <div className="flex-grow-1 pe-3">
          {isEditing ? (
            <input
              type="text"
              className="form-control mb-2 fw-semibold"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Task title"
              autoFocus
            />
          ) : (
            <h5 className="mb-2 text-body fw-bold">{task.title}</h5>
          )}
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {/* Status Dropdown */}
            <div className="dropdown">
              <button
                className={`btn btn-sm btn-${getCurrentStatus().bg} dropdown-toggle d-flex align-items-center gap-1`}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={isEditing}
              >
                <i className={`bi ${getCurrentStatus().icon}`}></i>
                {getCurrentStatus().label}
              </button>
              <ul className="dropdown-menu">
                {statusOptions.map(option => (
                  <li key={option.value}>
                    <button
                      className={`dropdown-item d-flex align-items-center gap-2 ${task.status === option.value || (task.status === 'done' && option.value === 'completed') ? 'active' : ''}`}
                      onClick={() => handleStatusChange(option.value)}
                    >
                      <i className={`bi ${option.icon} text-${option.bg}`}></i>
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {isEditing ? (
              <select 
                className="form-select form-select-sm" 
                style={{ width: 'auto' }}
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value as 'low' | 'medium' | 'high')}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            ) : (
              getPriorityBadge(task.priority)
            )}
            {isEditing ? (
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 'auto' }}
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
              />
            ) : (
              task.dueDate && (
                <Badge bg="light" className="text-dark">
                  <i className="bi bi-calendar me-1"></i>
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                </Badge>
              )
            )}
            {!isEditing && task.feed && task.feed.length > 0 && (
              <Badge bg="success">{task.feed.length} update{task.feed.length !== 1 ? 's' : ''}</Badge>
            )}
          </div>
        </div>
        <div className="d-flex gap-1">
          {isEditing ? (
            <>
              <Button variant="light-success" size="sm" onClick={handleSaveEdit} disabled={isSaving} title="Save">
                {isSaving ? <Spinner size="sm" animation="border" /> : <i className="bi bi-check-lg"></i>}
              </Button>
              <Button variant="light" size="sm" onClick={handleCancelEdit} disabled={isSaving} title="Cancel">
                <i className="bi bi-x-lg"></i>
              </Button>
            </>
          ) : (
            <>
              {!supportTask && <Button variant="light-primary" size="sm" onClick={handleStartEdit} title="Edit Task">
                <i className="bi bi-pencil"></i>
              </Button>}
              {!supportTask && <Button variant="light-danger" size="sm" onClick={() => setShowDeleteConfirm(true)} title="Delete Task">
                <i className="bi bi-trash"></i>
              </Button>}
              <Button 
                variant="light-success" 
                size="sm" 
                onClick={() => setShowAddUpdate(!showAddUpdate)}
                title="Add Update"
              >
                <i className="bi bi-plus-lg"></i>
              </Button>
              <Button variant="light" size="sm" onClick={onClose}>
                <i className="bi bi-x-lg"></i>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Task Body */}
      <div className="flex-grow-1 overflow-auto p-4 bg-body">
        {/* Description */}
        {isEditing ? (
          <div className="mb-4 pb-4 border-bottom">
            <label className="form-label small text-muted">Description</label>
            <textarea
              className="form-control"
              rows={6}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Add a description..."
            />
          </div>
        ) : (
          task.description && (
            <div className="mb-4 pb-4 border-bottom task-description-content">
              {containsHtml(task.description) ? (
                <HtmlContentWithCopyButtons html={task.description} />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {task.description}
                </ReactMarkdown>
              )}
            </div>
          )
        )}

        {/* Add Update Form */}
        <Collapse in={showAddUpdate}>
          <div className="mb-4">
            <div className="border rounded p-4 bg-light-dark">
              <textarea
                className="form-control mb-2"
                rows={4}
                placeholder="Add a note or update... (paste images with Ctrl+V)"
                value={updateContent}
                onChange={(e) => setUpdateContent(e.target.value)}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  
                  for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.type.startsWith('image/')) {
                      e.preventDefault();
                      const file = item.getAsFile();
                      if (file) {
                        const extension = item.type.split('/')[1] || 'png';
                        const namedFile = new File(
                          [file], 
                          `pasted-image-${Date.now()}.${extension}`,
                          { type: file.type }
                        );
                        setSelectedFiles(prev => [...prev, namedFile]);
                        toast.info('Image pasted');
                      }
                      break;
                    }
                  }
                }}
                disabled={isSaving}
              />
              
              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-2">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="d-flex align-items-center p-2 bg-body rounded mb-1">
                      {file.type.startsWith('image/') ? (
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt="Preview" 
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }}
                          className="me-2"
                        />
                      ) : (
                        <i className="bi bi-file-earmark me-2 text-primary"></i>
                      )}
                      <span className="small text-truncate flex-grow-1">{file.name}</span>
                      <span className="small text-muted me-2">{formatFileSize(file.size)}</span>
                      {uploadProgress[`file_${idx}`] !== undefined && (
                        <ProgressBar 
                          now={uploadProgress[`file_${idx}`]} 
                          style={{ width: '60px', height: '4px' }} 
                          className="me-2"
                        />
                      )}
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="p-0 text-danger"
                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                        disabled={isSaving}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex justify-content-between">
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelection}
                    style={{ display: 'none' }}
                  />
                  <Button 
                    variant="light" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving}
                  >
                    <i className="bi bi-paperclip me-1"></i>Attach
                  </Button>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    variant="light" 
                    size="sm"
                    onClick={() => { setShowAddUpdate(false); setUpdateContent(''); setSelectedFiles([]); }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="success" 
                    size="sm"
                    onClick={handleAddUpdate}
                    disabled={(!updateContent.trim() && selectedFiles.length === 0) || isSaving}
                  >
                    {isSaving ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-send me-1"></i>Send</>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Collapse>

        {/* Feed/Updates */}
        {sortedFeed.length > 0 ? (
          <div className="task-feed">
            {sortedFeed.length > 0 && (
              <div className="d-flex align-items-center mb-3">
                <span className="fw-semibold text-body small text-uppercase ls-1">Updates</span>
                <div className="flex-grow-1 ms-3 border-bottom"></div>
              </div>
            )}
            {sortedFeed.map((entry) => {
              const getIconInfo = () => {
                if (entry.type === 'file') {
                  return { icon: 'bi-paperclip', bg: 'bg-info', title: 'File attachment' };
                }
                if (entry.source === 'llm') {
                  return { icon: 'bi-robot', bg: 'bg-primary', title: 'AI assistant update' };
                }
                return { icon: 'bi-person-fill', bg: 'bg-success', title: 'Manual update' };
              };
              const iconInfo = getIconInfo();

              return (
              <div key={entry.id} className="d-flex mb-4 feed-entry-row">
                <div className="symbol symbol-35px me-3 flex-shrink-0" title={iconInfo.title}>
                  <div className={`symbol-label ${iconInfo.bg}`}>
                    <i className={`bi ${iconInfo.icon} text-white fs-7`}></i>
                  </div>
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-muted small fw-semibold">{iconInfo.title}</span>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">{formatTimestamp(entry.timestamp)}</small>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-muted flex-shrink-0 feed-delete-btn"
                        onClick={() => handleDeleteFeedEntry(entry.id)}
                        title="Delete"
                      >
                        <i className="bi bi-trash fs-7"></i>
                      </Button>
                    </div>
                  </div>
                  <div className="bg-light-dark rounded p-4">
                    <div className="feed-content">
                      {containsHtml(entry.content) ? (
                        <HtmlContentWithCopyButtons html={entry.content} />
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {entry.content}
                        </ReactMarkdown>
                      )}
                    </div>
                    {entry.attachment && (
                      <div 
                        className="d-flex align-items-center p-3 bg-body rounded mt-3 cursor-pointer border"
                        onClick={() => entry.fileUrl && handleDownloadFile(entry)}
                        style={{ cursor: entry.fileUrl ? 'pointer' : 'not-allowed' }}
                      >
                        <i className="bi bi-file-earmark-text me-3 text-info fs-4"></i>
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-semibold text-truncate">{entry.attachment.name}</div>
                          <small className="text-muted">{formatFileSize(entry.attachment.size)}</small>
                        </div>
                        {entry.fileUrl && <i className="bi bi-download text-info fs-5"></i>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            <div ref={feedEndRef} />
          </div>
        ) : !task.description && (
          <div className="text-center text-muted py-5">
            <i className="bi bi-chat-dots fs-1 mb-3 d-block opacity-50"></i>
            <p className="mb-1">No updates yet</p>
            <p className="small mb-0 text-body-tertiary">Click the + button above to add one.</p>
          </div>
        )}
      </div>
    </div>
    {supportTask && supportOutcome && <SupportDutyDialog task={supportTask} status={supportOutcome} onClose={() => setSupportOutcome(null)} onSave={async input => {
      const result = await monthlySupportApi.updateDuty(input);
      onTaskUpdated?.(result.task);
      return result;
    }} />}
    </>
  );
};

export default TaskDetailView;
